/**
 * ELF-CIRCLE — Who We Are (design artboard `#1c`).
 *
 * A full-bleed film band carrying the nav, breadcrumb and headline, then three
 * numbered blocks, a pull quote with stats, and the two next-step links.
 *
 * Static page — imagery is a bundled asset rather than an editable CMS slot.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import ElfFooter from '../ElfFooter';
import ElfPageNav, { ElfCrumb } from '../ElfPageNav';
import ElfShell from '../ElfShell';
import { useConsult } from '../elfConsult';
import { route } from '../../../../../shared/utils';
import bandImage from '../../../../../assets/elf/who-we-are-room.jpg';

const BLOCKS = [
  {
    num: '01',
    head: 'The Intersection',
    body:
      'ELF-CIRCLE INC. is a boutique marketing and web-development firm built at the intersection of three cultures that have always moved the world: Entertainment, Lifestyle, and Fashion.',
    tag: 'Three cultures, one address.',
  },
  {
    num: '02',
    head: 'From the Floor of the Room',
    body:
      "We didn't arrive here from a boardroom. We arrived here from the floor of the room — from grassroots activations that filled venues before algorithms existed to fill them, to the stages of Lincoln Center, NJPAC, and the Detroit Institute of Arts, where culture isn't just marketed, it's witnessed. Thirty-five years of building audiences one relationship at a time, before we ever built one with a pixel.",
    tag: 'Rooms filled before algorithms could fill them.',
  },
  {
    num: '03',
    head: 'Learned Through the Door',
    body:
      "That distinction matters. A generation of agencies learned culture through a dashboard. We learned it through the door — who shows up, who they bring with them, what makes them stay, and what makes them tell someone else. Everything we've since built in digital, SEO, GEO, social, and web architecture is that same instinct, translated into new terrain.",
    tag: 'Instinct, translated into new terrain.',
  },
];

const STATS = [
  { n: '35', l: 'Years in the room' },
  { n: '3', l: 'Cultures, one firm' },
  { n: '1:1', l: 'Audience, built' },
];

export const WhoWeAre: React.FC = () => {
  const consult = useConsult();
  return (
  <ElfShell>
    <div className="elf-page">
      <div className="elf-page__panel">
        <div className="elf-page__bleed" style={{ position: 'relative', height: 560, overflow: 'hidden' }}>
          <img className="bandimg bandimg--colour" src={bandImage} alt="" />
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(to top,rgba(0,0,0,.92) 4%,rgba(0,0,0,.42) 52%,rgba(0,0,0,.62))',
            }}
          />
          <div
            className="elf-page__col"
            style={{
              position: 'relative',
              zIndex: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <ElfPageNav current="/who-we-are" onFilm />
            <div className="wnav-onfilm" style={{ padding: '22px var(--pgut) 0' }}>
              <ElfCrumb trail={[{ label: 'Home', to: '/' }, 'Who We Are']} />
            </div>
            <div
              style={{
                marginTop: 'auto',
                padding: '0 var(--pgut) 52px',
                display: 'flex',
                flexDirection: 'column',
                gap: 20,
              }}
            >
              <span
                className="eyebrow"
                style={{
                  alignSelf: 'flex-start',
                  borderColor: 'rgba(255,255,255,.28)',
                  background: 'rgba(255,255,255,.1)',
                  color: 'rgba(255,255,255,.8)',
                }}
              >
                <i />
                Who we are
              </span>
              <h1 className="elf-page__h1 elf-page__h1--film">
                <span className="mask">
                  <span>We arrived from the floor</span>
                </span>
                <span className="mask">
                  <span style={{ animationDelay: '.12s' }}>
                    of the <span style={{ color: 'var(--acc-hero)' }}>room.</span>
                  </span>
                </span>
              </h1>
              <p
                style={{
                  margin: 0,
                  font: "400 15px/1.6 'Space Mono',monospace",
                  color: 'rgba(255,255,255,.72)',
                  maxWidth: '56ch',
                }}
              >
                Thirty-five years of building audiences one relationship at a time, before we ever
                built one with a pixel.
              </p>
            </div>
          </div>
        </div>

        <div
          className="elf-page__grid3"
          style={{
            padding: '56px var(--pgut) 24px',
            display: 'grid',
            gridTemplateColumns: 'repeat(3,1fr)',
            gap: 18,
            alignItems: 'stretch',
          }}
        >
          {BLOCKS.map((b) => (
            <div
              className="wblock"
              key={b.num}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
                padding: '30px 30px 34px',
                borderRadius: 18,
                border: '1px solid rgba(var(--panel-fg-rgb),.12)',
                background: 'rgba(var(--panel-fg-rgb),.035)',
              }}
            >
              <span className="wnum">{b.num}</span>
              <span className="whead" style={{ fontSize: 19 }}>
                {b.head}
              </span>
              <span className="wbar" />
              <p className="wbody" style={{ fontSize: 15.5, lineHeight: 1.72 }}>
                {b.body}
              </p>
              <span className="wtag" style={{ fontSize: 12, marginTop: 'auto' }}>
                {b.tag}
              </span>
            </div>
          ))}
        </div>

        <div
          className="elf-page__split"
          style={{
            padding: '26px var(--pgut) 44px',
            display: 'grid',
            gridTemplateColumns: '1fr 360px',
            gap: 56,
            alignItems: 'center',
            borderTop: '1px solid rgba(var(--panel-fg-rgb),.1)',
            marginTop: 32,
          }}
        >
          <p className="wpull" style={{ margin: 0 }}>
            We&rsquo;re not a legacy agency pretending to be new. We&rsquo;re not a new agency
            pretending to have history. We are both, on purpose.
          </p>
          {/* Inline, so the breakpoint can't reach it — the variable is only
              defined below 620px, where three 115px tracks break the labels. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'var(--gstat, repeat(3,1fr))', gap: 12 }}>
            {STATS.map((s) => (
              <div className="statx" key={s.l}>
                <span className="statn">{s.n}</span>
                <span className="statl">{s.l}</span>
              </div>
            ))}
          </div>
        </div>

        <div
          className="elf-page__grid2"
          style={{
            padding: '20px var(--pgut) 72px',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
            borderTop: '1px solid rgba(var(--panel-fg-rgb),.1)',
          }}
        >
          <Link className="nextlink" to={route('/services')}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              <span className="statl">Next</span>
              <span className="whead" style={{ fontSize: 20 }}>
                What we do
              </span>
              <span className="wtag" style={{ fontSize: 12.5 }}>
                Marketing, web development, events.
              </span>
            </div>
            <span className="arw">→</span>
          </Link>
          <button
              type="button"
              className="nextlink"
              onClick={() => consult?.open({ source: 'whoweare.consult', title: 'Book a consultation' })}
            ><div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              <span className="statl">Start here</span>
              <span className="whead" style={{ fontSize: 20 }}>
                Book a consultation
              </span>
              <span className="wtag" style={{ fontSize: 12.5 }}>
                Thirty minutes, no deck required.
              </span>
            </div>
            <span className="arw">→</span>
          </button>
        </div>
      </div>
    </div>
    <ElfFooter />
  </ElfShell>
  );
};

export default WhoWeAre;
